// Modules
import { useState } from "react"
import moment from "moment"
import { Link, useNavigate } from "react-router-dom"

// Constants
import PATHS, { getFullPath } from "~constants/paths"

// RBAC
import { RBAC_RULES } from "~rbac/constants/rbac-rules"
import IsAuthorized from "~rbac/isAuthorized"
import checkAuthorization from "~rbac/utils/checkAuthorization"

// Utils
import { isPerkActive } from "./utils/perk"
import { getFullImagePath } from "~utils/utils"

// Components
import ListItemMenu from "~components/ListItemMenu"
import LoadingTable from "~components/LoadingTable"
import { CompanyLogo } from "~components/companies/styled/company"
import SkeletonWrapper from "~components/SkeletonWrapper"
import AppSimulator from "~components/app-simulator"
import PerkDetailScreen from "~components/app-simulator/PerkDetailScreen"
import AppSimulatorModal from "~components/app-simulator/AppSimulatorModal"
import { ActiveStatus, InactiveStatus } from "~styled/status-badget"
import {
  Th,
  Td,
  Table,
  TableLink,
  NoTableData,
  TableContainer,
  Tr,
} from "~components/styled/table"

// Interfaces
import { PerkShape, PostPerkShape } from "~shapes/perk"
import { ListItemMenuProps } from "~components/ListItemMenu"
import { STATUS } from "~constants/status"
import { ButtonPrimarySoftLarge } from "~styled/button"
import { ACTIONS } from "~pages/private/perks"

const perkDetailPath = (id: string): string => {
  return `${getFullPath(PATHS.perks)}/${id}`
}

export interface PerksListProps {
  perks: PerkShape[]
  isLoading: boolean
  onClickPerkEdit?: (perk: PerkShape) => void
  onChangeStatus?: (perk: Partial<PostPerkShape>) => void
  action: ACTIONS
  onClickRedeemButton?: (perk: PerkShape) => void
}

const PerksList = ({
  perks,
  isLoading,
  onClickPerkEdit,
  onChangeStatus,
  action,
  onClickRedeemButton,
}: PerksListProps) => {
  const navigate = useNavigate()

  const [previewPerk, setPreviewPerk] = useState<PerkShape | null>(null)

  const isFirstLoad: boolean = !perks && isLoading
  const noPerksFound: boolean = !perks && !isLoading

  const statusIndicator = (perk: PerkShape) => {
    return isPerkActive(perk) ? <ActiveStatus /> : <InactiveStatus />
  }

  const handleToggleStatus = (perk: PerkShape) => {
    if (perk.id) {
      const status: STATUS =
        perk.status === STATUS.ACTIVE ? STATUS.INACTIVE : STATUS.ACTIVE
      const dataToUpdate: Partial<PostPerkShape> = {
        id: perk.id,
        status: status,
      }
      if (onChangeStatus) {
        onChangeStatus(dataToUpdate)
      }
    }
  }

  const handlePreview = (perk: PerkShape) => {
    setPreviewPerk(perk)
  }

  const handlePerkView = (id: string) => {
    navigate(perkDetailPath(id), { replace: false })
  }

  const getSkeletonColumns = (): number => {
    return (checkAuthorization(RBAC_RULES.SEE_MESSAGE_COMPANY) && 7) || 6
  }

  const listMenuProps = (perk: PerkShape) => {
    let props: Partial<ListItemMenuProps> = {
      onPreview: () => handlePreview(perk),
    }

    // View Permissions
    if (checkAuthorization(RBAC_RULES.VIEW_PERK_DETAILS)) {
      props.onView = () => handlePerkView(perk.id)
    }

    // Edit Permissions
    if (checkAuthorization(RBAC_RULES.UPDATE_PERK)) {
      if (onClickPerkEdit) {
        props.onEdit = () => onClickPerkEdit(perk)
      }
    }

    // Change Status Permissions
    if (checkAuthorization(RBAC_RULES.UPDATE_PERK_STATUS)) {
      props.onToggleStatus = () => handleToggleStatus(perk)
    }

    return props
  }

  const handleRedeem = (perk: PerkShape) => {
    if (onClickRedeemButton) {
      onClickRedeemButton(perk)
    }
  }

  return (
    <>
      <TableContainer>
        <Table>
          <thead>
            <tr>
              <Th style={{ width: "20px" }} />
              <IsAuthorized action={RBAC_RULES.SEE_PERK_COMPANY}>
                <>
                  <Th style={{ width: "70px" }} />
                  <Th style={{ width: "20%" }}>Company</Th>
                </>
              </IsAuthorized>
              <Th style={{ width: "20%" }}>Perk title</Th>
              <Th style={{ width: "25%" }}>Description</Th>
              <Th style={{ width: "100px" }}>Date</Th>
              <Th style={{ width: "70px" }}></Th>
            </tr>
          </thead>

          <tbody>
            {/* Loading */}
            {isFirstLoad && (
              <LoadingTable rowHeight="75px" columns={getSkeletonColumns()}>
                <Td>
                  <SkeletonWrapper
                    isLoading={true}
                    className="skeleton-status"
                  />
                </Td>
                <IsAuthorized action={RBAC_RULES.SEE_PERK_COMPANY}>
                  <Td>
                    <SkeletonWrapper
                      isLoading={true}
                      className="skeleton-image"
                      width={60}
                    />
                  </Td>
                </IsAuthorized>
              </LoadingTable>
            )}

            {perks &&
              perks.map((perk: PerkShape) => (
                <Tr key={perk.id}>
                  {/* Status */}
                  <Td>
                    <SkeletonWrapper
                      isLoading={isLoading}
                      className="skeleton-status"
                    >
                      {statusIndicator(perk)}
                    </SkeletonWrapper>
                  </Td>

                  {/* Logo and Company Name */}
                  <IsAuthorized action={RBAC_RULES.SEE_PERK_COMPANY}>
                    <>
                      {/* Logo */}
                      <Td>
                        <SkeletonWrapper
                          isLoading={isLoading}
                          className="skeleton-image"
                          width={60}
                        >
                          <CompanyLogo
                            src={getFullImagePath(
                              perk.logo || perk.company.logo
                            )}
                          />
                        </SkeletonWrapper>
                      </Td>

                      {/* Company name */}
                      <Td>
                        <SkeletonWrapper isLoading={isLoading}>
                          <TableLink>
                            <Link to={perkDetailPath(perk.id)}>
                              {perk.company?.name}
                            </Link>
                          </TableLink>
                        </SkeletonWrapper>
                      </Td>
                    </>
                  </IsAuthorized>

                  {/* Perk title */}
                  <Td>
                    <SkeletonWrapper isLoading={isLoading} width={40}>
                      <div style={{ maxWidth: "200px" }}>{perk.name}</div>
                    </SkeletonWrapper>
                  </Td>

                  {/* Description */}
                  <Td>
                    <SkeletonWrapper isLoading={isLoading} width={500}>
                      <div style={{ maxWidth: "400px" }}>
                        {perk.description}
                      </div>
                    </SkeletonWrapper>
                  </Td>

                  {/* Created */}
                  <Td>
                    <SkeletonWrapper isLoading={isLoading}>
                      <div>
                        {moment(perk.createdAt, "YYYY-MM-DD HH:mm:ss").format(
                          "MM/DD/YYYY"
                        )}
                      </div>
                    </SkeletonWrapper>
                  </Td>

                  {/* Actions */}
                  <Td>
                    <SkeletonWrapper isLoading={isLoading}>
                      {action === ACTIONS.MENU ? (
                        <ListItemMenu
                          isActive={isPerkActive(perk)}
                          {...listMenuProps(perk)}
                        />
                      ) : (
                        <ButtonPrimarySoftLarge
                          onClick={() => handleRedeem(perk)}
                        >
                          Redeem
                        </ButtonPrimarySoftLarge>
                      )}
                    </SkeletonWrapper>
                  </Td>
                </Tr>
              ))}
          </tbody>
        </Table>

        {/* No results found */}
        {noPerksFound && <NoTableData>No perks found...</NoTableData>}
      </TableContainer>

      {!!previewPerk && (
        <AppSimulatorModal
          isOpen={!!previewPerk}
          onRequestClose={() => setPreviewPerk(null)}
        >
          <AppSimulator>
            <PerkDetailScreen perk={previewPerk!} />
          </AppSimulator>
        </AppSimulatorModal>
      )}
    </>
  )
}

export default PerksList
